/* eslint-disable react-hooks/rules-of-hooks */
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isEqual } from 'lodash-es';

import { IBringgUuids, ILocation } from '@rbi-ctg/frontend';
import { IServerOrder } from '@rbi-ctg/menu';
import { IRestaurant } from '@rbi-ctg/store';
import { useApiKey } from 'hooks/configs/use-api-key';
import { useConfigValue } from 'hooks/configs/use-config-value';
import useMap from 'hooks/use-map';
import { CenterPoint, MarkerTypes } from 'hooks/use-map/types';
import { Bringg } from 'utils/bringg-sdk';

interface IUseOrderConfirmationMap {
  bringgUuids: IBringgUuids;
  enableDriverLocation: boolean;
  serverOrder: IServerOrder;
  store: IRestaurant;
}

export default function useDriverLocation({
  bringgUuids,
  enableDriverLocation,
  serverOrder,
  store,
}: IUseOrderConfirmationMap): ReactNode {
  const bringgRegion = useConfigValue({ key: 'bringg', defaultValue: {} })?.region;
  const apiKey = useApiKey({ key: 'bringg' });

  const [markerCoords, setMarkerCoords] = useState<Array<ILocation>>([]);
  const isDeliveryOrder = !!serverOrder.delivery;

  const destinationLatitude = serverOrder?.delivery?.dropoff?.latitude ?? 0;
  const destinationLongitude = serverOrder?.delivery?.dropoff?.longitude ?? 0;
  const pickupLatitude = serverOrder?.delivery?.pickup?.latitude ?? 0;
  const pickupLongitude = serverOrder?.delivery?.pickup?.longitude ?? 0;

  const mapCenter = useMemo(
    () =>
      isDeliveryOrder
        ? {
            lat: destinationLatitude,
            lng: destinationLongitude,
          }
        : { lat: store.latitude ?? 0, lng: store.longitude ?? 0 },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const driverMarkerId = useRef('');

  const { map, createMarker, fitAndCenterFromCoords, updateMarkerPosition } = useMap({
    disableControls: false,
    position: mapCenter,
  });

  /**
   * This sets up connection to the bringg sdk and adds
   * a handler to listen to location change of the driver.
   *
   * Documentation Here:
   * https://www.npmjs.com/package/@bringg/customer-sdk#dont-forget-to-set-your-own-keys-and-params
   *
   * But probably more thorough documentation is in the example here:
   * https://github.com/bringg/customer-js-sdk/blob/master/demo/customer-sdk-demo.js
   */
  const { share_uuid, order_uuid } = bringgUuids;

  const updateDriverLocationOnMap = useCallback(
    (location?: CenterPoint) => {
      if (!location?.lat || !location?.lng) {
        return;
      }

      if (updateMarkerPosition && driverMarkerId.current) {
        updateMarkerPosition(driverMarkerId.current, location);
      } else {
        driverMarkerId.current =
          createMarker({
            id: 'driver',
            type: MarkerTypes.Driver,
            location,
          }) ?? '';
      }

      fitAndCenterFromCoords([...markerCoords, location]);
    },
    [createMarker, fitAndCenterFromCoords, markerCoords, updateMarkerPosition]
  );

  useEffect(() => {
    if (!isDeliveryOrder || !enableDriverLocation || !order_uuid) {
      return;
    }
    const deliveryTracker = new Bringg(apiKey);
    const creds = { order_uuid, share_uuid, access_token: apiKey };
    const orderUpdateSubscription = deliveryTracker.watchOrder(creds, ({ driver }) =>
      updateDriverLocationOnMap(driver)
    );

    const locationChangeSubscription = deliveryTracker.locationUpdate(
      creds,
      updateDriverLocationOnMap
    );

    return () => {
      orderUpdateSubscription.unsubscribe();
      locationChangeSubscription.unsubscribe();
      deliveryTracker.disconnect();
    };

    // Destructured `order_uuid` & `share_uuid` to primitives so this hook reruns less
    // frequently and doesn't disconnect and reconnect to bringg on every update.
  }, [
    apiKey,
    bringgRegion,
    createMarker,
    enableDriverLocation,
    fitAndCenterFromCoords,
    isDeliveryOrder,
    markerCoords,
    order_uuid,
    share_uuid,
    updateDriverLocationOnMap,
  ]);

  // Creates store & destination markers on map & updates map view to fit them.
  useEffect(() => {
    const mapCoords: ILocation[] = [];
    if (pickupLatitude && pickupLongitude) {
      const pickupCoords = { lat: pickupLatitude, lng: pickupLongitude };
      createMarker({
        id: 'pickupCoords',
        type: MarkerTypes.StoreOpen,
        location: pickupCoords,
        onPress: () => null,
      });
      mapCoords.push(pickupCoords);
    }
    if (destinationLatitude && destinationLongitude) {
      const destinationCoord = { lat: destinationLatitude, lng: destinationLongitude };
      createMarker({
        id: 'destinationCoord',
        type: MarkerTypes.Destination,
        location: destinationCoord,
        onPress: () => null,
      });
      mapCoords.push(destinationCoord);
    }
    fitAndCenterFromCoords(mapCoords);
    if (!isEqual(markerCoords, mapCoords)) {
      setMarkerCoords(mapCoords);
    }
  }, [
    createMarker,
    destinationLatitude,
    destinationLongitude,
    fitAndCenterFromCoords,
    mapCenter,
    markerCoords,
    pickupLatitude,
    pickupLongitude,
  ]);
  return map;
}
